import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import {
  useGetEscalationTeams,
  useGetKBArticles,
  useGetTools,
  useGetDispositionVersions,
  useGetDispositionProperties,
  invalidateDispositionVersions,
  usePostDisposition,
  usePatchDispositionVersion,
} from 'actions/chatbot';
import StatusBadge from 'screens/chatbot/components/status-badge';
import PublishButton from 'screens/chatbot/components/publish-button';
import VersionsPanel from 'screens/chatbot/components/versions-panel';
import { UnpublishButton, RestoreButton } from 'screens/chatbot/components/state-buttons';

import { USER_TYPE, DESCRIPTION_MAX_LENGTH } from './constants';
import {
  FormContainer,
  FormHeader,
  FormTitle,
  PathText,
  Field,
  Label,
  Input,
  TextArea,
  CharCount,
  FormSelect,
  Toggle,
  ButtonRow,
  SaveButton,
  DeleteButton,
  SecondaryButtonRow,
  ScopeDropdownWrapper,
  ScopeDropdownTrigger,
  ScopeDropdownMenu,
  ScopeDropdownItem,
  ScopeChipsContainer,
  ScopeChip,
  ScopeChipRemove,
  DropdownArrow,
  DropdownEmptyItem,
  HelpText,
  WarningBox,
  WarningTitle,
  WarningItem,
  MultiFormSelect,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalText,
  ModalActions,
  ModalCancelButton,
  ModalConfirmDeleteButton,
} from './styled-comps';

const EDITABLE_FIELDS = [
  'display_name',
  'description',
  'bot_instructions',
  'model',
  'can_ai_resolve',
  'hidden_from_customer',
  'escalation_team',
  'kb_scope',
  'channels',
  'available_tools',
  'visibility_rules',
];

// Disposition path L1 prefixes that bucket the picker by user_type.
const PATH_ROOT = { PARENT: 'parent', TEACHER: 'teacher' };

// Resolve which user_type's flags apply for a given disposition path.
// L1 root determines the bucket. Anything else falls back to the union,
// since the backend doesn't enforce user_type scoping on rules — it just
// hides the disposition if the flag is missing from UserProperties.
function flagsForPath(path, catalog) {
  if (!catalog) return [];
  const root = (path || '').split('.')[0];
  if (root === PATH_ROOT.PARENT) return catalog[USER_TYPE.PARENT] || [];
  if (root === PATH_ROOT.TEACHER) return catalog[USER_TYPE.TEACHER] || [];
  const seen = new Set();
  return [
    ...(catalog[USER_TYPE.PARENT] || []),
    ...(catalog[USER_TYPE.TEACHER] || []),
  ].filter(f => {
    if (seen.has(f.key)) return false;
    seen.add(f.key);
    return true;
  });
}

// `escalation_team` on the API is a `{ region → team_id }` map.
//
// Region keys are exactly three buckets the backend resolves to:
//   - "US"      — parent country_code "1" or student.derived_region "US"
//   - "IND"     — parent country_code "91" or student.derived_region "IND"
//   - "ROW"     — every other parent country, or student.derived_region
//                 "UK"/"IS"/"ROW", or a parent with no country on file
// Plus the reserved "default" slot, used for tickets with no region —
// teacher-only queries (no student in conversation), or transient
// failures resolving the region. Default also covers any future region
// the backend might add before the admin UI is updated.
//
// Adding a region here is only meaningful if the backend can produce
// that key — see `app/support/region.py` in chatbackend.
const ESCALATION_REGIONS = [
  { key: 'default', label: 'Default (no region / unmatched)' },
  { key: 'US', label: 'United States (US)' },
  { key: 'IND', label: 'India (IND)' },
  { key: 'ROW', label: 'Rest of World (ROW)' },
];

function EditForm({
  disposition,
  channels = [],
  onSaved,
  onSelectVersion,
  isNew = false,
}) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newPath, setNewPath] = useState('');
  const [availableScopeTags, setAvailableScopeTags] = useState([]);
  const [registeredTools, setRegisteredTools] = useState([]);
  const [toolWarnings, setToolWarnings] = useState([]);
  const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const scopeDropdownRef = useRef(null);
  const toolsDropdownRef = useRef(null);
  const prevDispositionPathRef = useRef(null);

  const { get: getEscalationTeams, data: escalationTeamsData } = useGetEscalationTeams();
  const { get: getKBArticles, data: kbArticlesData } = useGetKBArticles();
  const { get: getToolsList, data: toolsData } = useGetTools();
  const { get: getVersions, data: versionsData } = useGetDispositionVersions();
  // `error` from createGetAPI's useGet is wired to `resource.error` but the
  // store actually keeps the error under `__ERROR__`, so `error` is always
  // undefined. `isProcessingFailed` is the reliable failure signal — flips
  // true on non-2xx / network failure and stays true until the next get().
  const {
    get: getDispositionProperties,
    data: dispositionPropertiesData,
    isProcessing: dispositionPropertiesLoading,
    isProcessingFailed: dispositionPropertiesFailed,
  } = useGetDispositionProperties();

  const handleSaveComplete = (errorMessage, data) => {
    setSaving(false);
    if (errorMessage) {
      dispatch(
        showNotification({
          text: `Failed: ${errorMessage}`,
          duration: 5,
          showCloseIcon: true,
        }),
      );
      return;
    }
    if (data && data.tool_warnings) {
      setToolWarnings(data.tool_warnings);
    }
    if (disposition && disposition.path) {
      invalidateDispositionVersions(disposition.path);
    }
    onSaved && onSaved(isNew ? newPath : null, form.channels);
  };

  const { post: postDisposition } = usePostDisposition({
    onComplete: handleSaveComplete,
  });
  const { patch: patchDispositionVersion } = usePatchDispositionVersion({
    onComplete: handleSaveComplete,
  });

  // Restore archived: POST {source_id} then PATCH status:PUBLISHED.
  const { post: postForRestore } = usePostDisposition({
    onComplete: (errorMessage, data) => {
      if (errorMessage || !data || !data.id) {
        setSaving(false);
        dispatch(
          showNotification({
            text: `Restore failed: ${errorMessage || 'unknown error'}`,
            duration: 5,
            showCloseIcon: true,
          }),
        );
        return;
      }
      patchDispositionVersion(data.id, { status: 'PUBLISHED' });
    },
  });

  const escalationTeams = Array.isArray(escalationTeamsData) ? escalationTeamsData : [];

  useEffect(() => {
    getEscalationTeams('list');
  }, [getEscalationTeams]);

  useEffect(() => {
    getKBArticles('all');
  }, [getKBArticles]);

  useEffect(() => {
    if (kbArticlesData) {
      const articles = Array.isArray(kbArticlesData) ? kbArticlesData : [];
      if (articles.length > 0) {
        const tagSet = new Set();
        articles.forEach(article => {
          if (article.scope) {
            article.scope.split(',').forEach(tag => {
              const trimmed = tag.trim();
              if (trimmed) tagSet.add(trimmed);
            });
          }
        });
        setAvailableScopeTags(Array.from(tagSet).sort());
      }
    }
  }, [kbArticlesData]);

  useEffect(() => {
    getToolsList('list');
  }, [getToolsList]);

  useEffect(() => {
    getDispositionProperties('catalog');
  }, [getDispositionProperties]);

  useEffect(() => {
    if (toolsData) {
      const tools = Array.isArray(toolsData) ? toolsData : [];
      if (tools.length > 0) {
        setRegisteredTools(tools.map(t => t.name));
      }
    }
  }, [toolsData]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (scopeDropdownRef.current && !scopeDropdownRef.current.contains(e.target)) {
        setScopeDropdownOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(e.target)) {
        setToolsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const dispositionPath = disposition && disposition.path;
  useEffect(() => {
    if (dispositionPath && !isNew) {
      getVersions(dispositionPath);
    }
  }, [dispositionPath, isNew, getVersions]);

  useEffect(() => {
    if (disposition) {
      const rules = (disposition.visibility_rules || {}).requires || [];
      setForm({
        display_name: disposition.display_name || '',
        description: disposition.description || '',
        bot_instructions: disposition.bot_instructions || '',
        model: disposition.model || 'sonnet',
        can_ai_resolve: disposition.can_ai_resolve || false,
        hidden_from_customer: disposition.hidden_from_customer || false,
        escalation_team:
          disposition.escalation_team && typeof disposition.escalation_team === 'object'
            ? { ...disposition.escalation_team }
            : {},
        kb_scope: disposition.kb_scope || '',
        channels: disposition.channels || [],
        available_tools: disposition.available_tools || [],
        visibility_rules: { requires: rules },
      });
      if (prevDispositionPathRef.current !== disposition.path) {
        setToolWarnings([]);
        prevDispositionPathRef.current = disposition.path;
      }
      setNewPath(disposition.path || '');
    }
  }, [disposition]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const selectedScopeTags = (form.kb_scope || '')
    .split(',')
    .map(t => t.trim())
    .filter(Boolean);

  const handleScopeToggle = tag => {
    const current = selectedScopeTags;
    const updated = current.includes(tag)
      ? current.filter(t => t !== tag)
      : [...current, tag];
    handleChange('kb_scope', updated.join(','));
  };

  const handleScopeRemove = tag => {
    const updated = selectedScopeTags.filter(t => t !== tag);
    handleChange('kb_scope', updated.join(','));
  };

  const fieldPayload = () => {
    const payload = {};
    EDITABLE_FIELDS.forEach(key => {
      if (form[key] !== undefined) payload[key] = form[key];
    });
    return payload;
  };

  const status = disposition && disposition.status;
  const isDraft = status === 'DRAFT';
  const isPublished = status === 'PUBLISHED';
  const hiddenNeedsChannel = !!form.hidden_from_customer && !(form.channels || []).length;
  const savedHiddenNeedsChannel =
    !!disposition?.hidden_from_customer && !(disposition?.channels || []).length;
  const isArchived = status === 'ARCHIVED';
  const publishBlocked = savedHiddenNeedsChannel && ((isDraft && !isNew) || isArchived);
  const readOnly = !isNew && !isDraft;

  const handleSaveDraft = () => {
    setSaving(true);
    setToolWarnings([]);
    if (isNew) {
      postDisposition({ path: newPath, ...fieldPayload() });
    } else {
      patchDispositionVersion(disposition.id, fieldPayload());
    }
  };

  const handleEditPublished = () => {
    setSaving(true);
    setToolWarnings([]);
    postDisposition({ source_id: disposition.id });
  };

  const handlePublish = () => {
    setSaving(true);
    patchDispositionVersion(disposition.id, { status: 'PUBLISHED' });
  };

  const handleUnpublish = () => {
    setSaving(true);
    patchDispositionVersion(disposition.id, { status: 'ARCHIVED' });
  };

  const handleRestore = () => {
    setSaving(true);
    postForRestore({ source_id: disposition.id });
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    patchDispositionVersion(disposition.id, { is_deleted: true });
  };

  const handleSelectVersion = version => {
    if (onSelectVersion) onSelectVersion(version);
  };

  if (!disposition) return null;

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>
          {isNew ? 'New Disposition' : disposition.display_name || disposition.name}
        </FormTitle>
        {!isNew && <StatusBadge status={status} />}
      </FormHeader>
      {isNew ? (
        <Field>
          <Label>Path (e.g. scheduling.class.missed)</Label>
          <Input
            value={newPath}
            onChange={e => setNewPath(e.target.value)}
            placeholder="parent.child.name"
          />
        </Field>
      ) : (
        <PathText>{disposition.path}</PathText>
      )}

      <Field>
        <Label>Display Name</Label>
        <Input
          value={form.display_name || ''}
          onChange={e => handleChange('display_name', e.target.value)}
          disabled={readOnly}
        />
      </Field>

      <Field>
        <Label>Description</Label>
        <HelpText>
          One or two clear sentences on what this covers &mdash; and what it does not. The
          bot matches incoming messages against this, so full sentences route better than
          keywords or fragments. Up to {DESCRIPTION_MAX_LENGTH} characters; leave blank to
          fall back to the display name.
        </HelpText>
        <TextArea
          rows={3}
          maxLength={DESCRIPTION_MAX_LENGTH}
          value={form.description || ''}
          onChange={e => handleChange('description', e.target.value)}
          disabled={readOnly}
        />
        <CharCount>
          {(form.description || '').length} / {DESCRIPTION_MAX_LENGTH}
        </CharCount>
      </Field>

      <Field>
        <Label>Bot Instructions</Label>
        <TextArea
          rows={15}
          value={form.bot_instructions || ''}
          onChange={e => handleChange('bot_instructions', e.target.value)}
          disabled={readOnly}
        />
      </Field>

      <Field>
        <Label>Model</Label>
        <FormSelect
          value={form.model || 'sonnet'}
          onChange={e => handleChange('model', e.target.value)}
          disabled={readOnly}
        >
          <option value="sonnet">Sonnet</option>
          <option value="haiku">Haiku</option>
        </FormSelect>
      </Field>

      <Field>
        <Toggle>
          <input
            type="checkbox"
            checked={form.can_ai_resolve || false}
            onChange={e => handleChange('can_ai_resolve', e.target.checked)}
            disabled={readOnly}
          />
          Can AI Resolve
        </Toggle>
      </Field>

      <Field>
        <Toggle>
          <input
            type="checkbox"
            checked={form.hidden_from_customer || false}
            onChange={e => handleChange('hidden_from_customer', e.target.checked)}
            disabled={readOnly}
          />
          Hidden from customers
        </Toggle>
        <HelpText>
          Keeps this disposition out of the customer&apos;s options. The bot can still
          route to it, and agents can still assign it. Needs at least one channel, or no
          app will offer it.
        </HelpText>
      </Field>

      <Field>
        <Label>Escalation Teams by Region</Label>
        <HelpText>
          Pick a team per customer region. <b>Default</b> is used when the ticket has no
          region — teacher-only queries, or any case where the backend couldn&apos;t
          resolve one. For parent tickets the region is set from the customer&apos;s
          country at ticket creation; for teacher tickets it&apos;s set at escalation time
          if the bot has been talking about a specific paid student. Leave a region row
          blank to fall through to <b>Default</b>.
        </HelpText>
        {ESCALATION_REGIONS.map(region => {
          const teamMap = form.escalation_team || {};
          const value = teamMap[region.key] || '';
          return (
            <div
              key={region.key}
              style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}
            >
              <span style={{ width: 200 }}>{region.label}</span>
              <FormSelect
                value={value}
                onChange={e => {
                  const next = { ...(form.escalation_team || {}) };
                  if (e.target.value) {
                    next[region.key] = e.target.value;
                  } else {
                    delete next[region.key];
                  }
                  handleChange('escalation_team', next);
                }}
                disabled={readOnly}
              >
                <option value="">— None —</option>
                {escalationTeams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </option>
                ))}
              </FormSelect>
            </div>
          );
        })}
      </Field>

      <Field>
        <Label>KB Scope</Label>
        <ScopeDropdownWrapper ref={scopeDropdownRef}>
          <ScopeDropdownTrigger
            $open={scopeDropdownOpen}
            $hasValue={selectedScopeTags.length > 0}
            onClick={() => !readOnly && setScopeDropdownOpen(prev => !prev)}
          >
            <span>
              {selectedScopeTags.length > 0
                ? `${selectedScopeTags.length} scope${
                    selectedScopeTags.length > 1 ? 's' : ''
                  } selected`
                : 'Select scope tags...'}
            </span>
            <DropdownArrow>{scopeDropdownOpen ? '\u25B2' : '\u25BC'}</DropdownArrow>
          </ScopeDropdownTrigger>
          {scopeDropdownOpen && !readOnly && (
            <ScopeDropdownMenu>
              {availableScopeTags.length === 0 && (
                <DropdownEmptyItem>No scope tags found</DropdownEmptyItem>
              )}
              {availableScopeTags.map(tag => (
                <ScopeDropdownItem key={tag}>
                  <input
                    type="checkbox"
                    checked={selectedScopeTags.includes(tag)}
                    onChange={() => handleScopeToggle(tag)}
                  />
                  {tag}
                </ScopeDropdownItem>
              ))}
            </ScopeDropdownMenu>
          )}
        </ScopeDropdownWrapper>
        {selectedScopeTags.length > 0 && (
          <ScopeChipsContainer>
            {selectedScopeTags.map(tag => (
              <ScopeChip key={tag}>
                {tag}
                {!readOnly && (
                  <ScopeChipRemove onClick={() => handleScopeRemove(tag)}>
                    &times;
                  </ScopeChipRemove>
                )}
              </ScopeChip>
            ))}
          </ScopeChipsContainer>
        )}
      </Field>

      {channels.length > 0 && (
        <Field>
          <Label>Channels</Label>
          <MultiFormSelect
            multiple
            size={Math.min(channels.length + 1, 6)}
            value={form.channels || []}
            onChange={e => {
              const selected = Array.from(e.target.selectedOptions, opt => opt.value);
              handleChange('channels', selected);
            }}
            disabled={readOnly}
          >
            {channels.map(ch => (
              <option key={ch.name} value={ch.name}>
                {ch.display_name || ch.name}
              </option>
            ))}
          </MultiFormSelect>
          <HelpText>Hold Ctrl/Cmd to select multiple</HelpText>
        </Field>
      )}

      {(() => {
        // Loading: show the field with a help-line hint so the admin
        // sees the section is coming, not silently missing.
        if (dispositionPropertiesLoading) {
          return (
            <Field>
              <Label>Visibility Rules (customer must match all)</Label>
              <HelpText>Loading available flags…</HelpText>
            </Field>
          );
        }
        // Error: surface it instead of hiding the section, so the admin
        // knows the picker isn't available because of a fetch failure
        // rather than a config decision.
        if (dispositionPropertiesFailed) {
          return (
            <Field>
              <Label>Visibility Rules (customer must match all)</Label>
              <HelpText>
                Could not load visibility flags. Try refreshing the page.
              </HelpText>
            </Field>
          );
        }
        const visibilityFlags = flagsForPath(
          (disposition && disposition.path) || newPath,
          dispositionPropertiesData,
        );
        if (visibilityFlags.length === 0) return null;
        const selected = (form.visibility_rules || {}).requires || [];
        return (
          <Field>
            <Label>Visibility Rules (customer must match all)</Label>
            <MultiFormSelect
              multiple
              size={Math.min(visibilityFlags.length + 1, 6)}
              value={selected}
              onChange={e => {
                const next = Array.from(e.target.selectedOptions, opt => opt.value);
                handleChange('visibility_rules', { requires: next });
              }}
              disabled={readOnly}
            >
              {visibilityFlags.map(f => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </MultiFormSelect>
            <HelpText>
              Empty = visible to everyone. Hold Ctrl/Cmd to select multiple.
            </HelpText>
          </Field>
        );
      })()}

      <Field>
        <Label>Available Tools</Label>
        <ScopeDropdownWrapper ref={toolsDropdownRef}>
          <ScopeDropdownTrigger
            $open={toolsDropdownOpen}
            $hasValue={(form.available_tools || []).length > 0}
            onClick={() => !readOnly && setToolsDropdownOpen(prev => !prev)}
          >
            <span>
              {(form.available_tools || []).length > 0
                ? `${(form.available_tools || []).length} tool${
                    (form.available_tools || []).length > 1 ? 's' : ''
                  } selected`
                : 'Select tools...'}
            </span>
            <DropdownArrow>{toolsDropdownOpen ? '\u25B2' : '\u25BC'}</DropdownArrow>
          </ScopeDropdownTrigger>
          {toolsDropdownOpen && !readOnly && (
            <ScopeDropdownMenu>
              {registeredTools.length === 0 && (
                <DropdownEmptyItem>No tools registered</DropdownEmptyItem>
              )}
              {registeredTools.map(tool => (
                <ScopeDropdownItem key={tool}>
                  <input
                    type="checkbox"
                    checked={(form.available_tools || []).includes(tool)}
                    onChange={() => {
                      const current = form.available_tools || [];
                      const updated = current.includes(tool)
                        ? current.filter(t => t !== tool)
                        : [...current, tool];
                      handleChange('available_tools', updated);
                    }}
                  />
                  {tool}
                </ScopeDropdownItem>
              ))}
            </ScopeDropdownMenu>
          )}
        </ScopeDropdownWrapper>
        {(form.available_tools || []).length > 0 && (
          <ScopeChipsContainer>
            {(form.available_tools || []).map(tool => (
              <ScopeChip key={tool}>
                {tool}
                {!readOnly && (
                  <ScopeChipRemove
                    onClick={() =>
                      handleChange(
                        'available_tools',
                        (form.available_tools || []).filter(t => t !== tool),
                      )
                    }
                  >
                    &times;
                  </ScopeChipRemove>
                )}
              </ScopeChip>
            ))}
          </ScopeChipsContainer>
        )}
      </Field>

      {(hiddenNeedsChannel || publishBlocked) && (
        <WarningBox>
          <WarningTitle>Needs a channel</WarningTitle>
          <WarningItem>
            A disposition hidden from customers still needs a channel — without one, no
            app will offer it.
            {publishBlocked ? ' Publishing is blocked until then.' : ''}
            {publishBlocked && !hiddenNeedsChannel
              ? ' Save the draft to apply your change.'
              : ''}
          </WarningItem>
        </WarningBox>
      )}

      {toolWarnings.length > 0 && (
        <WarningBox>
          <WarningTitle>Tool Warnings</WarningTitle>
          {toolWarnings.map((w, i) => (
            <WarningItem key={i}>{w}</WarningItem>
          ))}
        </WarningBox>
      )}

      <ButtonRow>
        {(isNew || isDraft) && (
          <SaveButton
            onClick={handleSaveDraft}
            disabled={saving}
            label={saving ? 'Saving...' : isNew ? 'Save as Draft' : 'Save Draft'}
          />
        )}
        {isDraft && !isNew && (
          <PublishButton onPublish={handlePublish} disabled={saving || publishBlocked} />
        )}
        {isPublished && (
          <>
            <SaveButton
              onClick={handleEditPublished}
              disabled={saving}
              label={saving ? 'Creating draft...' : 'Edit (creates draft)'}
            />
            <UnpublishButton
              onUnpublish={handleUnpublish}
              disabled={saving}
              noun="disposition"
            />
          </>
        )}
        {isArchived && (
          <RestoreButton onRestore={handleRestore} disabled={saving || publishBlocked} />
        )}
      </ButtonRow>

      {!isNew && isDraft && (
        <SecondaryButtonRow>
          <DeleteButton
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving}
            label="Delete"
          />
        </SecondaryButtonRow>
      )}

      {!isNew && (
        <VersionsPanel
          versions={versionsData}
          selectedId={disposition.id}
          onSelect={handleSelectVersion}
        />
      )}

      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>
              Are you sure you want to delete{' '}
              {disposition.display_name || disposition.name}?
            </ModalTitle>
            <ModalText>This action cannot be undone.</ModalText>
            <ModalActions>
              <ModalCancelButton
                onClick={() => setShowDeleteConfirm(false)}
                label="Cancel"
              />
              <ModalConfirmDeleteButton label="Delete" onClick={handleDelete} />
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </FormContainer>
  );
}

export default EditForm;
